import cookie from 'react-cookies';
import { REFRESH_COOKIE, TOKEN_COOKIE } from './cookies';

const BLY_RECOMMENDATIONS_KEY = 'bly-recommendations-done';
export const REFRESH_EXPIRES_AT_KEY = 'beon_refresh_expires_at';

/** Renova o access um pouco antes do `exp` do JWT. */
const ACCESS_REFRESH_SKEW_SECONDS = 15;

let redirectingToLogin = false;
let accessTimer: ReturnType<typeof setTimeout> | null = null;
let logoutTimer: ReturnType<typeof setTimeout> | null = null;
let cookiePollTimer: ReturnType<typeof setInterval> | null = null;
let cookieFocusHandler: (() => void) | null = null;
let refreshHandler: (() => Promise<boolean>) | null = null;

const COOKIE_INTEGRITY_POLL_MS = 2_000;

const loadCookie = (name: string): string | undefined => {
  try {
    const value = cookie.load(name) as string | undefined;
    return value || undefined;
  } catch {
    return undefined;
  }
};

/** Há vestígios de sessão no localStorage (login anterior). */
export const hasPersistedAuthState = (): boolean => {
  try {
    return Boolean(
      localStorage.getItem('current_user') ||
      localStorage.getItem('current_user_acl') ||
      localStorage.getItem(REFRESH_EXPIRES_AT_KEY),
    );
  } catch {
    return false;
  }
};

export const areAuthCookiesMissing = (): boolean =>
  !loadCookie(TOKEN_COOKIE) && !loadCookie(REFRESH_COOKIE);

const ensureAuthCookieIntegrity = (): void => {
  if (hasPersistedAuthState() && areAuthCookiesMissing()) {
    expireSessionToLogin();
  }
};

const startCookieIntegrityWatch = (): void => {
  stopCookieIntegrityWatch();
  if (typeof window === 'undefined') return;

  cookieFocusHandler = () => ensureAuthCookieIntegrity();
  window.addEventListener('focus', cookieFocusHandler);
  cookiePollTimer = setInterval(
    ensureAuthCookieIntegrity,
    COOKIE_INTEGRITY_POLL_MS,
  );
};

const stopCookieIntegrityWatch = (): void => {
  if (cookiePollTimer) {
    clearInterval(cookiePollTimer);
    cookiePollTimer = null;
  }
  if (cookieFocusHandler && typeof window !== 'undefined') {
    window.removeEventListener('focus', cookieFocusHandler);
    cookieFocusHandler = null;
  }
};

export const resetSessionRedirectFlag = (): void => {
  redirectingToLogin = false;
};

export const registerRefreshHandler = (
  handler: (() => Promise<boolean>) | null,
): void => {
  refreshHandler = handler;
};

export const stopSessionWatch = (): void => {
  if (accessTimer) {
    clearTimeout(accessTimer);
    accessTimer = null;
  }
  if (logoutTimer) {
    clearTimeout(logoutTimer);
    logoutTimer = null;
  }
  stopCookieIntegrityWatch();
};

export const persistRefreshExpiry = (expiresInSeconds: number): void => {
  const at = Math.floor(Date.now() / 1000) + Math.floor(expiresInSeconds);
  localStorage.setItem(REFRESH_EXPIRES_AT_KEY, String(at));
};

export const isRefreshSessionExpired = (): boolean => {
  try {
    const raw = localStorage.getItem(REFRESH_EXPIRES_AT_KEY);
    const at = raw ? Number(raw) : NaN;
    if (!Number.isFinite(at)) return false;
    return at <= Math.floor(Date.now() / 1000);
  } catch {
    return false;
  }
};

export const readJwtExp = (token: string | null | undefined): number | null => {
  if (!token || token.split('.').length < 2) return null;
  try {
    const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = payload + '='.repeat((4 - (payload.length % 4)) % 4);
    const json = globalThis.atob(padded);
    const claims = JSON.parse(json) as { exp?: unknown };
    return typeof claims.exp === 'number' ? claims.exp : null;
  } catch {
    return null;
  }
};

const secondsUntil = (unix: number): number =>
  Math.max(0, unix - Math.floor(Date.now() / 1000));

/** Access JWT → refresh; refresh TTL → logout. Sem timer de logout no access. */
export const startSessionWatch = (): void => {
  stopSessionWatch();
  if (typeof window === 'undefined') return;

  startCookieIntegrityWatch();
  ensureAuthCookieIntegrity();
  if (redirectingToLogin) return;

  const now = Math.floor(Date.now() / 1000);
  if (isRefreshSessionExpired()) {
    expireSessionToLogin();
    return;
  }
  const refreshAtRaw = localStorage.getItem(REFRESH_EXPIRES_AT_KEY);
  const refreshAt = refreshAtRaw ? Number(refreshAtRaw) : NaN;

  if (Number.isFinite(refreshAt) && refreshAt > now) {
    logoutTimer = setTimeout(
      () => {
        expireSessionToLogin();
      },
      secondsUntil(refreshAt) * 1000,
    );
  }

  let token: string | undefined;
  token = loadCookie(TOKEN_COOKIE);
  const accessExp = readJwtExp(token);
  if (!accessExp) return;

  if (accessExp <= now) {
    if (refreshHandler) {
      void refreshHandler().then(ok => {
        if (!ok) expireSessionToLogin();
        else startSessionWatch();
      });
    }
    return;
  }

  const delaySec = Math.max(1, accessExp - now - ACCESS_REFRESH_SKEW_SECONDS);
  accessTimer = setTimeout(() => {
    if (!refreshHandler) {
      expireSessionToLogin();
      return;
    }
    void refreshHandler().then(ok => {
      if (!ok) expireSessionToLogin();
      else startSessionWatch();
    });
  }, delaySec * 1000);
};

export const clearSession = (): void => {
  stopSessionWatch();
  try {
    cookie.remove(TOKEN_COOKIE, { path: '/' });
    cookie.remove(REFRESH_COOKIE, { path: '/' });
  } catch {
    // ignore cookie errors
  }

  const blyRecommendationsDone = localStorage.getItem(BLY_RECOMMENDATIONS_KEY);
  localStorage.clear();
  if (blyRecommendationsDone) {
    localStorage.setItem(BLY_RECOMMENDATIONS_KEY, blyRecommendationsDone);
  }
};

/** Limpa sessão e recarrega /login (full load). Também na própria tela de login. */
export const expireSessionToLogin = (): void => {
  if (redirectingToLogin) return;
  redirectingToLogin = true;
  clearSession();

  if (typeof window === 'undefined') return;
  window.location.replace('/login');
};

/** Expira na hora se cookies sumiram ou o refresh TTL já passou — antes do 1º render. */
export const expireSessionIfStale = (): boolean => {
  if (typeof window === 'undefined') return false;
  if (redirectingToLogin) return true;
  if (hasPersistedAuthState() && areAuthCookiesMissing()) {
    expireSessionToLogin();
    return true;
  }
  if (!isRefreshSessionExpired()) return false;
  expireSessionToLogin();
  return true;
};
